'use client';

import React, { useState, useEffect, PropsWithRef, type ReactElement } from 'react';
import Icon from '../icon';
import { generateUniqueId } from '@fshr/shared';
import { useI18n, useLocale } from '@fshr/context/i18n-context';

export type Props = {
	id?: string;
	className?: string;
	leftLabel?: string;
	rightLabel?: string;
	theme?: 'light' | 'dark';
	defaultChecked?: boolean;
	checked?: boolean;
	onChange?: (checked: boolean) => void;
	disabled?: boolean;
	ariaLabel?: string;
};

export default function Switch({
	id = generateUniqueId('switch'),
	className = '',
	leftLabel,
	rightLabel,
	theme = 'light',
	defaultChecked = false,
	checked,
	onChange,
	disabled = false,
	ariaLabel,
}: PropsWithRef<Props>): ReactElement {
	const [isChecked, setIsChecked] = useState(defaultChecked);
	const { locale, t } = useI18n();

	useEffect(() => {
		if (checked !== undefined) {
			setIsChecked(checked);
		}
	}, [checked]);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newChecked = event.target.checked;
		setIsChecked(newChecked);
		onChange?.(newChecked);
	};

	return (
		<div
			className={`flex items-center space-x-2 ${
				theme === 'dark' ? 'text-white' : 'text-black'
			} ${className}`}
		>
			{disabled && <Icon icon="lock" />}
			{leftLabel && <span className="text-sm font-medium">{leftLabel}</span>}
			<label className="relative inline-flex items-center cursor-pointer" aria-label={ariaLabel}>
				<input
					type="checkbox"
					name={id}
					id={id}
					checked={isChecked}
					onChange={handleChange}
					className="sr-only peer"
					disabled={disabled}
				/>
				<div
					className={`
						relative
						w-[50px]
						h-[30px]
						rounded-full
						transition-colors
						duration-200
						ease-in-out
						${isChecked ? 'bg-[#030303]' : 'bg-[#E6E6E6]'}
						peer-focus:outline
						peer-focus:outline-solid
						peer-focus:outline-2
					`}
				>
					<div
						className={`
							absolute
							top-[1.5px]
							w-[27px]
							h-[27px]
							bg-white
							rounded-full
							transition-transform
							duration-200
							ease-in-out
							${locale === 'ar' ? `${isChecked ? 'translate-x-[-21.5px]' : 'translate-x-[-1.5px]'}` : `${isChecked ? 'translate-x-[21.5px]' : 'translate-x-[1.5px]'}`}
						`}
					/>
				</div>
			</label>
			{rightLabel && <span className="text-sm font-medium">{rightLabel}</span>}
		</div>
	);
}
